import React from 'react';
import { useAppContext } from '../contexts/AppContext';
import ConfirmModal from '../components/ConfirmModal';

const JOURNAL_MOODS = [
  { id: 'happy',      emoji: '😊', label: 'Happy' },
  { id: 'calm',       emoji: '😌', label: 'Calm' },
  { id: 'reflective', emoji: '🤔', label: 'Reflective' },
  { id: 'sad',        emoji: '😢', label: 'Sad' },
  { id: 'frustrated', emoji: '😤', label: 'Frustrated' },
  { id: 'grateful',   emoji: '🙏', label: 'Grateful' },
  { id: 'motivated',  emoji: '💪', label: 'Motivated' },
  { id: 'tired',      emoji: '😴', label: 'Tired' },
];

export default function JournalPage() {
  const {
    journalEntries, saveJournalEntries, showToast,
    journalText, setJournalText, journalMood, setJournalMood,
    journalAudioBlob, setJournalAudioBlob, journalAudioUrl, setJournalAudioUrl,
    journalAudioPlayUrl, journalRecording, journalRecordingTime, setJournalRecordingTime,
    startJournalRecording, stopJournalRecording,
    savingJournal, setSavingJournal,
    viewingJournalEntry, setViewingJournalEntry,
    editingJournalEntry, setEditingJournalEntry, editJournalText, setEditJournalText,
    transcribeAudio, saveJournalAudioToVault, groupJournalByDate,
  } = useAppContext();

  const [confirmDeleteEntryId, setConfirmDeleteEntryId] = React.useState(null);

  // ── Viewing a single entry ──
  if (viewingJournalEntry) {
    const entry = viewingJournalEntry;
    const moodObj = JOURNAL_MOODS.find(m => m.id === entry.mood);
    const isEditing = editingJournalEntry === entry.id;

    return (
      <>
      <div>
        <button onClick={() => { setViewingJournalEntry(null); setEditingJournalEntry(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: '#3c4653', fontFamily: "'Lora', serif", padding: 0, marginBottom: '20px', display: 'block' }}>
          ← Back to Journal
        </button>

        <div className="card" style={{ padding: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <h3 style={{ fontSize: '1.3rem', color: '#0f172a', marginBottom: '6px' }}>
                {entry.createdAt ? new Date(entry.createdAt).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'Journal Entry'}
              </h3>
              {moodObj && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: 'rgba(15,23,42,0.04)', borderRadius: '20px', fontSize: '1rem', color: '#3c4653' }}>
                  {moodObj.emoji} {moodObj.label}
                </span>
              )}
            </div>
            <span style={{ fontSize: '1rem', color: '#3c4653' }}>
              {entry.createdAt ? new Date(entry.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
            </span>
          </div>

          {!isEditing ? (
            <>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem', color: '#0f172a', lineHeight: 1.9, whiteSpace: 'pre-wrap', marginBottom: '24px' }}>
                {entry.content}
              </p>
              {entry.audioPath && journalAudioPlayUrl && (
                <audio src={journalAudioPlayUrl} controls style={{ width: '100%', marginBottom: '20px' }} />
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-ghost" onClick={() => { setEditingJournalEntry(entry.id); setEditJournalText(entry.content); }}>
                  Edit
                </button>
                <button className="btn btn-ghost" style={{ color: '#c0392b' }} onClick={() => setConfirmDeleteEntryId(entry.id)}>
                  Delete
                </button>
              </div>
            </>
          ) : (
            <>
              <textarea
                value={editJournalText}
                onChange={e => setEditJournalText(e.target.value)}
                rows={8}
                style={{ width: '100%', padding: '16px', borderRadius: '12px', border: '1.5px solid rgba(15,23,42,0.12)', fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a', background: '#fff', outline: 'none', resize: 'vertical', lineHeight: 1.8, boxSizing: 'border-box', marginBottom: '16px' }}
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-primary" onClick={() => {
                  const updated = journalEntries.map(e => e.id === entry.id ? { ...e, content: editJournalText } : e);
                  saveJournalEntries(updated);
                  setViewingJournalEntry({ ...entry, content: editJournalText });
                  setEditingJournalEntry(null);
                  showToast('Updated!', 'success');
                }}>Save Changes</button>
                <button className="btn btn-ghost" onClick={() => setEditingJournalEntry(null)}>Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
      {confirmDeleteEntryId && (
        <ConfirmModal
          title="Remove this entry?"
          message="This can't be undone."
          confirmLabel="Delete"
          cancelLabel="Keep it"
          destructive
          onConfirm={() => {
            const updated = journalEntries.filter(e => e.id !== confirmDeleteEntryId);
            saveJournalEntries(updated);
            setViewingJournalEntry(null);
            setConfirmDeleteEntryId(null);
            showToast('Entry deleted.', 'success');
          }}
          onCancel={() => setConfirmDeleteEntryId(null)}
        />
      )}
      </>
    );
  }

  // ── Save handler (shared by voice + text) ──
  const handleSaveJournal = async (opts = {}) => {
    const { transcribeFirst } = opts;
    const hasText = journalText.trim();
    const hasAudio = !!journalAudioBlob;
    if (!hasText && !hasAudio) return;

    setSavingJournal(true);
    try {
      let content = journalText.trim();

      // Transcribe first if requested
      if (transcribeFirst && hasAudio) {
        const text = await transcribeAudio(journalAudioBlob);
        if (text) content = content ? content + '\n\n' + text : text;
      }

      // Save audio to vault
      let audioPath = null;
      if (hasAudio) { audioPath = await saveJournalAudioToVault(); }

      const newEntry = {
        id: `j_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        content,
        mood: journalMood,
        audioPath,
        createdAt: new Date().toISOString(),
      };
      const updated = [newEntry, ...journalEntries];
      saveJournalEntries(updated);
      setJournalText('');
      setJournalMood(null);
      setJournalAudioBlob(null);
      setJournalAudioUrl('');
      showToast('Journal entry saved.', 'success');
    } catch (err) { console.error(err); showToast('Failed to save.', 'error'); }
    setSavingJournal(false);
  };

  // ── Main Journal view ──
  const grouped = groupJournalByDate(journalEntries);
  const hasContent = journalText.trim() || journalAudioBlob;
  const showTextMode = !journalRecording && !journalAudioBlob && journalText.length > 0;

  return (
    <div>
      <div className="page-header">
        <h2>📓 Journal</h2>
        <p>A private space for your thoughts, reflections, and voice.</p>
      </div>

      {/* ── New Entry Card — compact, no-scroll ── */}
      <div className="card" style={{ padding: '24px', marginBottom: '8px' }}>

        {/* ── STATE 1: Idle — big record button ── */}
        {!journalRecording && !journalAudioBlob && !showTextMode && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={startJournalRecording}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px',
                padding: '22px 28px', background: '#0f172a', border: 'none', borderRadius: '14px',
                cursor: 'pointer', transition: 'all 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#3a342e'}
              onMouseOut={e => e.currentTarget.style.background = '#0f172a'}
            >
              <span style={{ fontSize: '1.4rem' }}></span>
              <span style={{ fontFamily: "'Lora', serif", fontSize: '1.15rem', fontWeight: 600, color: '#fbf8f1' }}>Record a thought</span>
            </button>
            <button
              onClick={() => setJournalText(' ')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653', padding: 0 }}
            >
              or write instead
            </button>
          </div>
        )}

        {/* ── STATE 2: Recording — timer + stop ── */}
        {journalRecording && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '18px 24px', background: 'rgba(200,0,0,0.04)', border: '1.5px solid rgba(200,0,0,0.2)', borderRadius: '14px' }}>
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#e74c3c', animation: 'pulse 1.5s infinite', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem', fontWeight: 600, color: '#0f172a' }}>Recording...</div>
              <div style={{ fontSize: '1rem', color: '#3c4653' }}>{Math.floor(journalRecordingTime / 60)}:{(journalRecordingTime % 60).toString().padStart(2, '0')}</div>
            </div>
            <button onClick={stopJournalRecording}
              style={{ padding: '12px 28px', background: '#0f172a', border: 'none', borderRadius: '10px', color: '#fbf8f1', fontFamily: "'Lora', serif", fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer' }}>
              ⏹ Done
            </button>
          </div>
        )}

        {/* ── STATE 3: Audio captured — transcribe+save or just save ── */}
        {journalAudioBlob && !journalRecording && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', padding: '12px 16px', background: 'rgba(39,174,96,0.06)', borderRadius: '10px', border: '1px solid rgba(39,174,96,0.15)' }}>
              <span style={{ color: '#27ae60', fontSize: '1.1rem' }}>✓</span>
              <span style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a', flex: 1 }}>
                {Math.floor(journalRecordingTime / 60)}:{(journalRecordingTime % 60).toString().padStart(2, '0')} recorded
              </span>
              <audio src={journalAudioUrl} controls style={{ height: 32, maxWidth: '160px' }} />
            </div>

            {/* Mood — compact inline row */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
              {JOURNAL_MOODS.map(m => (
                <button key={m.id} onClick={() => setJournalMood(journalMood === m.id ? null : m.id)}
                  style={{
                    padding: '6px 12px', borderRadius: '20px', border: journalMood === m.id ? '1.5px solid #0f172a' : '1.5px solid rgba(15,23,42,0.08)',
                    background: journalMood === m.id ? 'rgba(15,23,42,0.06)' : '#fff', color: journalMood === m.id ? '#0f172a' : '#3c4653',
                    fontFamily: "'Nunito', sans-serif", fontSize: '0.95rem', cursor: 'pointer', transition: 'all 0.15s'
                  }}>
                  {m.emoji} {m.label}
                </button>
              ))}
            </div>

            {/* Action buttons — side by side */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => handleSaveJournal({ transcribeFirst: true })}
                disabled={savingJournal}
                className="btn btn-primary"
                style={{ flex: 1, minWidth: '160px', fontSize: '1.05rem', padding: '14px 20px', opacity: savingJournal ? 0.6 : 1 }}
              >
                {savingJournal ? 'Saving...' : '✦ Transcribe & Save'}
              </button>
              <button
                onClick={() => handleSaveJournal({ transcribeFirst: false })}
                disabled={savingJournal}
                className="btn btn-ghost"
                style={{ flex: 1, minWidth: '140px', fontSize: '1.05rem', padding: '14px 20px' }}
              >
                Save Audio Only
              </button>
            </div>

            {/* Discard + re-record */}
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '12px' }}>
              <button onClick={() => { setJournalAudioBlob(null); setJournalAudioUrl(''); setJournalRecordingTime(0); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Lora', serif", fontSize: '0.95rem', color: '#3c4653', padding: 0 }}>
                Discard
              </button>
              <button onClick={() => { setJournalAudioBlob(null); setJournalAudioUrl(''); setJournalRecordingTime(0); startJournalRecording(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Lora', serif", fontSize: '0.95rem', color: '#3c4653', padding: 0 }}>
                Re-record
              </button>
            </div>
          </div>
        )}

        {/* ── STATE 4: Text mode — textarea + mood + save ── */}
        {!journalRecording && !journalAudioBlob && showTextMode && (
          <div>
            <textarea
              value={journalText === ' ' ? '' : journalText}
              onChange={e => setJournalText(e.target.value)}
              placeholder="What's on your heart today?"
              rows={4}
              autoFocus
              style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid rgba(15,23,42,0.12)', fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a', background: '#fff', outline: 'none', resize: 'vertical', lineHeight: 1.8, boxSizing: 'border-box', marginBottom: '12px' }}
            />

            {/* Mood — compact */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
              {JOURNAL_MOODS.map(m => (
                <button key={m.id} onClick={() => setJournalMood(journalMood === m.id ? null : m.id)}
                  style={{
                    padding: '6px 12px', borderRadius: '20px', border: journalMood === m.id ? '1.5px solid #0f172a' : '1.5px solid rgba(15,23,42,0.08)',
                    background: journalMood === m.id ? 'rgba(15,23,42,0.06)' : '#fff', color: journalMood === m.id ? '#0f172a' : '#3c4653',
                    fontFamily: "'Nunito', sans-serif", fontSize: '0.95rem', cursor: 'pointer', transition: 'all 0.15s'
                  }}>
                  {m.emoji} {m.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                onClick={() => handleSaveJournal()}
                disabled={savingJournal || !journalText.trim()}
                className="btn btn-primary"
                style={{ fontSize: '1.05rem', padding: '14px 28px', opacity: (!journalText.trim()) ? 0.5 : 1 }}
              >
                {savingJournal ? 'Saving...' : 'Save Entry'}
              </button>
              <button onClick={startJournalRecording}
                style={{ padding: '12px 16px', background: 'none', border: '1px solid rgba(15,23,42,0.12)', borderRadius: '10px', cursor: 'pointer', fontSize: '1.1rem', color: '#3c4653', display: 'flex', alignItems: 'center', gap: '6px' }}>
                
              </button>
              <button onClick={() => { setJournalText(''); setJournalMood(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Lora', serif", fontSize: '0.95rem', color: '#3c4653', padding: 0 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Previous Entries ── */}
      {journalEntries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#3c4653' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '16px', opacity: 0.4 }}>📓</div>
          <p style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem', fontStyle: 'italic', marginBottom: '8px' }}>Your journal is waiting.</p>
          <p style={{ fontSize: '1rem', color: '#3c4653' }}>Record a thought, write a few words, or just check in with how you're feeling.</p>
        </div>
      ) : (
        <div style={{ marginTop: '12px' }}>
          {grouped.map(([label, entries]) => (
            <div key={label} style={{ marginBottom: '8px' }}>
              <h4 style={{ fontFamily: "'Lora', serif", fontSize: '0.9rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: '10px', paddingLeft: '4px' }}>
                {label}
              </h4>
              {entries.map(entry => {
                const moodObj = JOURNAL_MOODS.find(m => m.id === entry.mood);
                return (
                  <div key={entry.id} className="card" style={{ cursor: 'pointer', padding: '20px 24px', marginBottom: '8px' }}
                    onClick={() => setViewingJournalEntry(entry)}>
                    <div className="journal-entry-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: entry.content ? '8px' : 0, gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                        {moodObj && <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{moodObj.emoji}</span>}
                        <span style={{ fontFamily: "'Lora', serif", fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>
                          {entry.createdAt ? new Date(entry.createdAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Entry'}
                        </span>
                        {entry.audioPath && <span style={{ fontSize: '0.9rem', color: '#3c4653' }}></span>}
                      </div>
                      <span style={{ fontSize: '1rem', color: '#3c4653', flexShrink: 0 }}>
                        {entry.createdAt ? new Date(entry.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    {entry.content && (
                      <p style={{ color: '#3c4653', fontSize: '1.05rem', lineHeight: 1.7, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', margin: 0 }}>
                        {entry.content}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
